import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Collapse, Container } from 'reactstrap';
import withRouter from '../../Components/Common/withRouter';

import logoSm from '../../assets/images/qdosv5/App.png';

//i18n
import { withTranslation } from 'react-i18next';

// Import Data
import navdata from '../LayoutMenuData';
import VerticalLayout from '../VerticalLayouts';

//SimpleBar
import SimpleBar from 'simplebar-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import {
  setPinnedModulesState,
  setUnpinnedModules,
} from 'store/slices/common/sidebarSlice';

// Define types for menu items and props
type MenuItem = {
  id: string;
  label: string;
  icon?: string;
  link?: string;
  click: (e: React.MouseEvent) => void;
  subItems?: SubMenuItem[];
  stateVariables?: boolean;
  isHeader?: boolean;
};

type SubMenuItem = {
  id: string;
  label: string;
  link?: string;
  parentId: string;
  click?: (e: React.MouseEvent) => void;
  stateVariables?: boolean;
  isChildItem?: boolean;
  childItems?: ChildMenuItem[];
  badgeName?: string;
  badgeColor?: string;
};

type ChildMenuItem = {
  id: string | number;
  label: string;
  link?: string;
  click?: (e: React.MouseEvent) => void;
  stateVariables?: boolean;
  isChildItem?: boolean;
  childItems?: any[];
};

type PinnedModule = {
  module: string;
  isPinned: boolean;
};

type SideMenuProps = {
  navData: MenuItem[];
  onIconClick?: (id: string) => void;
};

type MainMenuProps = {
  navData: MenuItem[];
  pinnedModules: PinnedModule[];
  pinModule: (id: string) => void;
  unPinModule: (id: string) => void;
  t: (text: string) => string;
};

type SubMenuItemProps = {
  item: MenuItem;
  pinnedModules: PinnedModule[];
  pinModule: (id: string) => void;
  unPinModule: (id: string) => void;
  t: (text: string) => string;
};

type MenuItemLinkProps = {
  subItem: SubMenuItem;
  t: (text: string) => string;
  pinnedModules?: PinnedModule[];
  pinModule?: (id: string) => void;
  unPinModule?: (id: string) => void;
  parentLabel?: string;
};

// Logo Component
const Logo = () => (
  <Link to="#" className="logo">
    <img src={logoSm} alt="" height="22" />
  </Link>
);

// MenuIcon Component for sidebar icon
const MenuIcon = ({ item, onIconClick }: { item: MenuItem, onIconClick?: (id: string) => void }) => {
  const handleClick = (e: React.MouseEvent) => {
    item.click(e);
    if (onIconClick) onIconClick(item.id);
  };

  return item.subItems ? (
    <li>
      <Link
        onClick={handleClick}
        to="#"
        sub-items={item.id}
        className="nav-icon"
        data-bs-toggle="collapse"
        data-testid="nav-icon"
      >
        <i className={item.icon}></i>
      </Link>
    </li>
  ) : (
    <Link
      onClick={handleClick}
      to={item.link ? item.link : '/#'}
      sub-items={item.id}
      className="nav-icon"
      data-bs-toggle="collapse"
      data-testid="nav-icon"
    >
      <i className={item.icon}></i>
    </Link>
  );
};

// SideMenu Component for the icon view sidebar
const SideMenu = ({ navData, onIconClick }: SideMenuProps) => {
  return (
    <div id="two-column-menu" data-testid="two-column-menu">
      <SimpleBar className="twocolumn-iconview">
        <Logo />
        {(navData || []).map((item, key) => (
          <React.Fragment key={key}>
            {item.icon && <MenuIcon item={item} onIconClick={onIconClick} />}
          </React.Fragment>
        ))}
      </SimpleBar>
    </div>
  );
};

// MenuItemLink Component for rendering simple menu item links
const MenuItemLink = ({ subItem, t, pinnedModules, pinModule, unPinModule, parentLabel }: MenuItemLinkProps) => {
  const isPinned = pinnedModules?.find(m => m.module === subItem.id)?.isPinned;
  
  const handlePinClick = () => {
    if (isPinned) {
      unPinModule?.(subItem.id);
    } else {
      pinModule?.(subItem.id);
    }
  };

  return (
    <li className="nav-item">
      <div className="d-flex align-items-center pe-3">
        <Link
          to={subItem.link ? subItem.link : '/#'}
          className="nav-link flex-grow-1"
        >
          {t(subItem.label)}
          {subItem.badgeName && (
            <span
              className={`badge badge-pill bg-${subItem.badgeColor}`}
              data-key="t-new"
            >
              {subItem.badgeName}
            </span>
          )}
        </Link>
        {parentLabel === 'Modules' && pinnedModules && pinModule && unPinModule && (
          <i
            onClick={handlePinClick}
            className={`ri-pushpin-${isPinned ? 'fill' : 'line'}`}
          ></i>
        )}
      </div>
    </li>
  );
};

// NestedMenuItem Component for rendering nested menu items with children
const NestedMenuItem = ({ subItem, t, itemId }: { subItem: SubMenuItem, t: (text: string) => string, itemId: string }) => {
  return (
    <li className="nav-item">
      <Link
        onClick={subItem.click}
        className="nav-link"
        to="/#"
        data-bs-toggle="collapse"
      >
        {t(subItem.label)}
        {subItem.badgeName && (
          <span
            className={`badge badge-pill bg-${subItem.badgeColor}`}
            data-key="t-new"
          >
            {subItem.badgeName}
          </span>
        )}
      </Link>
      {subItem.childItems && (
        <Collapse
          className="menu-dropdown"
          isOpen={subItem.stateVariables}
          id={itemId}
        >
          <ul className="nav nav-sm flex-column">
            {(subItem.childItems || []).map((childItem, key) => (
              <li className="nav-item" key={key}>
                <Link
                  to={childItem.link ? childItem.link : '/#'}
                  onClick={childItem.click}
                  className="nav-link"
                >
                  {t(childItem.label)}
                </Link>
                {childItem.childItems && (
                  <Collapse
                    className="menu-dropdown"
                    isOpen={childItem.stateVariables}
                    id={itemId}
                  >
                    <ul className="nav nav-sm flex-column">
                      {childItem.isChildItem &&
                        (childItem.childItems || []).map((nestedChild, key) => (
                          <li className="nav-item" key={key}>
                            <Link
                              to={nestedChild.link ? nestedChild.link : '/#'}
                              className="nav-link"
                            >
                              {t(nestedChild.label)}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </Collapse>
                )}
              </li>
            ))}
          </ul>
        </Collapse>
      )}
    </li>
  );
};

// SubMenuItems Component for handling each submenu group
const SubMenuItems = ({ item, pinnedModules, pinModule, unPinModule, t }: SubMenuItemProps) => {
  if (!item.subItems) return null;

  return (
    <li className="nav-item">
      <Collapse
        className="menu-dropdown"
        isOpen={item.stateVariables}
        id={item.id}
        data-testid="menu-dropdown"
      >
        <ul className="nav nav-sm flex-column test">
          {(item.subItems || []).map((subItem, key) => (
            <React.Fragment key={key}>
              {!subItem.isChildItem ? (
                <MenuItemLink 
                  subItem={subItem} 
                  t={t} 
                  pinnedModules={pinnedModules} 
                  pinModule={pinModule} 
                  unPinModule={unPinModule}
                  parentLabel={item.label}
                />
              ) : (
                <NestedMenuItem subItem={subItem} t={t} itemId={item.id} />
              )}
            </React.Fragment>
          ))}
        </ul>
      </Collapse>
    </li>
  );
};

// MainMenu Component for the content/submenu sidebar
const MainMenu = ({ navData, pinnedModules, pinModule, unPinModule, t }: MainMenuProps) => {
  return (
    <SimpleBar id="navbar-nav" className="navbar-nav" data-testid="navbar-nav">
      {(navData || []).map((item, key) => (
        <React.Fragment key={key}>
          {item.subItems && (
            <SubMenuItems 
              item={item} 
              pinnedModules={pinnedModules} 
              pinModule={pinModule} 
              unPinModule={unPinModule} 
              t={t} 
            />
          )}
        </React.Fragment>
      ))}
    </SimpleBar>
  );
};

// Hook for managing sidebar menu state and actions
const useMenuHandlers = (props: any) => {
  const dispatch = useDispatch();
  const { pinnedModules, currentMenu } = useSelector(
    (state: RootState) => state.sidebarState
  );

  // Pin Module to Navbar through Redux
  const pinModule = (moduleName: string) => {
    if (pinnedModules.length >= 6) {
      toast.error('You can only pin 6 modules');
    } else {
      dispatch(setPinnedModulesState(moduleName));
    }
  };

  // Unpin Module from Navbar though Redux
  const unPinModule = (moduleName: string) => {
    dispatch(setUnpinnedModules(moduleName));
  };

  const path = props.router.location.pathname;

  const activateParentDropdown = useCallback((item: any) => {
    item.classList.add('active');
    let parentCollapseDiv = item.closest('.collapse.menu-dropdown');
    if (parentCollapseDiv) {
      parentCollapseDiv.classList.add('show');
      parentCollapseDiv.parentElement.children[0].classList.add('active');
      parentCollapseDiv.parentElement.children[0].setAttribute(
        'aria-expanded',
        'true'
      );
      if (parentCollapseDiv.parentElement.closest('.collapse.menu-dropdown')) {
        parentCollapseDiv.parentElement
          .closest('.collapse')
          .classList.add('show');
        const parentParentCollapse =
          parentCollapseDiv.parentElement.closest(
            '.collapse'
          ).previousElementSibling;
        if (parentParentCollapse) {
          parentParentCollapse.classList.add('active');
          if (parentParentCollapse.closest('.collapse.menu-dropdown')) {
            parentParentCollapse
              .closest('.collapse.menu-dropdown')
              .classList.add('show');
          }
        }
      }
      activateIconSidebarActive(parentCollapseDiv.getAttribute('id'));
      return false;
    }
    return false;
  }, []);

  function activateIconSidebarActive(id: any) {
    var menu = document.querySelector(
      "#two-column-menu .simplebar-content-wrapper a[sub-items='" +
        id +
        "'].nav-icon"
    );

    if (menu !== null) {
      menu.classList.add('active');
    }
  }

  const removeActivation = (items: any) => {
    let activeItems = items.filter((x: any) => x.classList.contains('active'));
    activeItems.forEach((item: any) => {
      if (item.classList.contains('menu-link')) {
        if (!item.classList.contains('active')) {
          item.setAttribute('aria-expanded', false);
        }
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove('show');
        }
      }
      if (item.classList.contains('nav-link')) {
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove('show');
        }
        item.setAttribute('aria-expanded', false);
      }
      item.classList.remove('active');
    });

    const ul = document.getElementById('two-column-menu') as HTMLElement;
    if (ul) {
      const iconItems: any = ul.getElementsByTagName('a');
      let itemsArray = [...iconItems];
      let activeIconItems = itemsArray.filter((x) =>
        x.classList.contains('active')
      );
      activeIconItems.forEach((item) => {
        item.classList.remove('active');
        var id = item.getAttribute('sub-items');
        if (id) {
          var getId = document.getElementById(id) as HTMLElement;
          if (getId) getId.classList.remove('show');
        }
      });
    }
  };

  const initMenu = useCallback(() => {
    const pathName = process.env.PUBLIC_URL + path;
    const ul = document.getElementById('navbar-nav') as HTMLElement;
    if (ul) {
      const items: any = ul.getElementsByTagName('a');
      let itemsArray = [...items]; // converts NodeList to Array
      removeActivation(itemsArray);
      let matchingMenuItem = itemsArray.find((x) => {
        return x.pathname === pathName;
      });
      if (matchingMenuItem) {
        activateParentDropdown(matchingMenuItem);
      } else {
        if (process.env.PUBLIC_URL) {
          var id = pathName.replace(process.env.PUBLIC_URL, '');
          id = id.replace('/', '');
        } else {
          id = pathName.replace('/', '');
        }
        if (id) document.body.classList.add('twocolumn-panel');
        activateIconSidebarActive(id);
      }
    }
  }, [path, activateParentDropdown]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    initMenu();
  }, [path, initMenu]);

  // Handle icon click
  const handleIconClick = (id: string) => {
    const menuItem = document.querySelector(`[sub-items="${id}"]`);
    if (menuItem) {
      const isActive = menuItem.classList.contains('active');
      if (!isActive) {
        const ul = document.getElementById('navbar-nav') as HTMLElement;
        if (ul) {
          const items: any = ul.getElementsByTagName('a');
          let itemsArray = [...items];
          removeActivation(itemsArray);
        }
        menuItem.classList.add('active');
        const collapseDiv = document.getElementById(id);
        if (collapseDiv) {
          collapseDiv.classList.add('show');
        }
      }
    }
  };

  return {
    pinnedModules,
    currentMenu,
    pinModule,
    unPinModule,
    activateParentDropdown,
    activateIconSidebarActive,
    removeActivation,
    initMenu,
    handleIconClick
  };
};

// Main TwoColumnLayout Component
const TwoColumnLayout = (props: any) => {
  const navData = navdata().props.children;
  const {
    pinnedModules,
    pinModule,
    unPinModule,
    initMenu,
    handleIconClick
  } = useMenuHandlers(props);

  // Resize sidebar
  const [isMenu, setIsMenu] = useState('twocolumn');
  const windowResizeHover = () => {
    initMenu();
    var windowSize = document.documentElement.clientWidth;
    if (windowSize < 767) {
      document.documentElement.setAttribute('data-layout', 'vertical');
      setIsMenu('vertical');
    } else {
      document.documentElement.setAttribute('data-layout', 'twocolumn');
      setIsMenu('twocolumn');
    }
  };

  useEffect(function setupListener() {
    if (props.layoutType === 'twocolumn') {
      window.addEventListener('resize', windowResizeHover);

      return function cleanupListener() {
        window.removeEventListener('resize', windowResizeHover);
      };
    }
  });
  
  return (
    <React.Fragment>
      {isMenu === 'twocolumn' ? (
        <div id="scrollbar">
          <Container fluid>
            <SideMenu navData={navData} onIconClick={handleIconClick} />
            <MainMenu 
              navData={navData}
              pinnedModules={pinnedModules}
              pinModule={pinModule}
              unPinModule={unPinModule}
              t={props.t}
            />
          </Container>
        </div>
      ) : (
        <SimpleBar id="scrollbar" className="h-100">
          <Container fluid>
            <div id="two-column-menu"></div>
            <ul className="navbar-nav" id="navbar-nav">
              <VerticalLayout />
            </ul>
          </Container>
        </SimpleBar>
      )}
    </React.Fragment>
  );
};

TwoColumnLayout.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(TwoColumnLayout));
